import React, { forwardRef, useImperativeHandle } from 'react'
import {
  Elements,
  useStripe,
  useElements,
  PaymentElement,
} from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'
import { STRIPE_KEY, MY_PGA_COM_HOST } from 'env'
import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import { LinearProgress } from '@mui/material'
import { useSnackbar, useSnackbarError } from 'lib/snackbar'
import useQueryString from 'lib/hooks/use-query-string'
import { FRISCO_COACHING_CENTER_SLUG } from 'utils/constants'
import { useSetMarketingOptIn } from 'components/marketing-opt-in/hooks'

const stripePromise = loadStripe(STRIPE_KEY)

// Inner component that uses Stripe hooks - must be inside Elements provider with clientSecret
const PaymentElementForm = forwardRef(
  (
    {
      clientSecret,
      isSubmitting,
      setIsSubmitting,
      coachSlug,
      isCoachingCenterPurchase,
      marketingOptInChecked,
    },
    ref,
  ) => {
    const stripe = useStripe()
    const elements = useElements()
    const { enqueueSnackbar } = useSnackbar()
    const snackbarError = useSnackbarError(enqueueSnackbar)
    const { studentId } = useQueryString()
    const { setMarketingOptIn } = useSetMarketingOptIn()

    const paymentElementOptions = {
      layout: 'tabs',
    }

    const submitPayment = async () => {
      if (!stripe || !elements || !clientSecret) {
        setIsSubmitting?.(false)
        return
      }
      setIsSubmitting(true)

      const billingDetails = {
        // We can add more billing details here as needed
      }

      const { error: submitError } = await elements.submit()
      if (submitError) {
        setIsSubmitting?.(false)
        snackbarError(submitError.message)
        return
      }

      if (marketingOptInChecked !== null) {
        setMarketingOptIn(marketingOptInChecked)
      }

      const returnUrl = isCoachingCenterPurchase
        ? `${MY_PGA_COM_HOST}/${FRISCO_COACHING_CENTER_SLUG}/${studentId}/purchase/lesson-pack`
        : `${MY_PGA_COM_HOST}/coach/${coachSlug}/purchase/lesson-pack`

      const { error } = await stripe.confirmPayment({
        elements,
        redirect: 'always',
        confirmParams: {
          expand: ['payment_method'],
          return_url: returnUrl,
          payment_method_data: {
            billing_details: billingDetails,
          },
        },
        clientSecret: clientSecret,
      })

      if (error) {
        snackbarError(error.message)
        setIsSubmitting?.(false)
      }
    }

    useImperativeHandle(ref, () => ({
      submitPayment,
    }))

    return (
      <PaymentElement id="payment-element" options={paymentElementOptions} />
    )
  },
)

// Outer component that provides Elements context with clientSecret
const StripePaymentElementsForm = forwardRef(
  (
    {
      clientSecret,
      secretLoading,
      isSubmitting,
      setIsSubmitting,
      coachSlug,
      isCoachingCenterPurchase,
      marketingOptInChecked,
    },
    ref,
  ) => {
    return (
      <>
        <Box mb={3} mt={4} pt={2}>
          <Typography variant="h4">Enter payment details</Typography>
        </Box>
        <Box mb={4}>
          <Typography variant="body">
            Your credit card information is not being stored in our system.
          </Typography>
        </Box>
        {secretLoading && <LinearProgress />}
        {clientSecret && (
          <Elements
            key={clientSecret}
            stripe={stripePromise}
            options={{
              clientSecret,
              appearance: {
                theme: 'stripe',
              },
            }}
          >
            <PaymentElementForm
              ref={ref}
              clientSecret={clientSecret}
              isSubmitting={isSubmitting}
              setIsSubmitting={setIsSubmitting}
              coachSlug={coachSlug}
              isCoachingCenterPurchase={isCoachingCenterPurchase}
              marketingOptInChecked={marketingOptInChecked}
            />
          </Elements>
        )}
      </>
    )
  },
)

export default StripePaymentElementsForm
